import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, LogIn, LogOut, Loader2 } from "lucide-react";
import { BrandMark } from "@/components/Nav";
import { FadeUp, MaskedLines } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export const APPS = [
  {
    slug: "core-suite",
    number: "01",
    title: "HARSight CORE Suite",
    tag: "TRH DEMO · FINANCE OS",
    modules: ["CAT", "RCT", "PRO", "PLAN", "DST"],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    repo: "harsight/core-suite-trh-demo",
    launch_url: "/api/apps/core-suite/",
    status: "LIVE",
  },
  {
    slug: "ht-design-lab",
    number: "02",
    title: "HT Design Lab",
    tag: "R&D · PROTOTYPES",
    modules: ["PATTERNS", "TOKENS", "MOTION"],
    image: "https://images.pexels.com/photos/18446450/pexels-photo-18446450.jpeg?auto=compress&cs=tinysrgb&w=1400",
    repo: "harsight/ht-design-lab",
    launch_url: "/api/apps/ht-design-lab/",
    status: "LIVE",
  },
  {
    slug: "ht-market-intel",
    number: "03",
    title: "HT Market Intel",
    tag: "MARKET INTELLIGENCE",
    modules: ["MARKET", "COMPETITORS", "TRACKER", "MODULES"],
    image: "https://images.unsplash.com/photo-1543286386-2e659306cd6c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    repo: "harsight/ht-market-intel",
    launch_url: "/api/apps/ht-market-intel/",
    status: "LIVE",
  },
  {
    slug: "slicedhealth-grow-readout",
    number: "04",
    title: "SlicedHealth / GROW",
    tag: "TORCH READINESS BRIEF",
    modules: ["SITUATION", "WEDGE", "BUILD PLAN", "DEMO"],
    image: "https://images.pexels.com/photos/5452230/pexels-photo-5452230.jpeg?auto=compress&cs=tinysrgb&w=1400",
    repo: "harsight/slicedhealth-grow-readout",
    launch_url: "/api/apps/slicedhealth-grow-readout/",
    status: "LIVE",
  },
  {
    slug: "harsight-multi-environment-showcase",
    number: "05",
    title: "Multi-Env Showcase",
    tag: "UI SHOWCASE",
    modules: ["DEV", "STAGING", "PROD", "PARITY"],
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    repo: "harsight/harsight-multi-environment-showcase",
    launch_url: "/api/apps/harsight-multi-environment-showcase/",
    status: "LIVE",
  },
  {
    slug: "ht-core-studio",
    number: "06",
    title: "HT CORE Studio",
    tag: "DESIGN STUDIO",
    modules: ["FAVORITES", "ANNOTATIONS", "AI RESTYLE", "VARIANTS"],
    image: "https://images.unsplash.com/photo-1558655146-9f40138edfeb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    repo: "harsight/ht-core-studio",
    launch_url: "/api/apps/ht-core-studio/",
    status: "LIVE",
  },
];

export async function launchApp(app) {
  if (!app.launch_url) {
    toast.info(`${app.title} — repository not available.`);
    return;
  }
  // Same-origin: browser sends the access_token cookie automatically.
  // Backend gates /api/apps/<slug>/* on that cookie.
  window.location.href = app.launch_url;
}

export default function Landing() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAuthed = user && typeof user === "object";

  return (
    <div className="relative min-h-screen" style={{ background: "var(--hs-bg)" }}>
      <div className="hs-noise" />

      {/* Minimal top bar */}
      <header className="container-hs pt-8 pb-6 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo-link">
          <BrandMark />
        </Link>
        <div className="flex items-center gap-3">
          {isAuthed ? (
            <>
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-hs hidden sm:inline">
                {user.email}
              </span>
              <button
                onClick={async () => { await logout(); nav("/"); }}
                className="hs-btn"
                data-testid="nav-logout"
              >
                <LogOut size={14} /> Sign out
              </button>
            </>
          ) : (
            <Link to="/login" className="hs-btn hs-btn-primary" data-testid="nav-login">
              <LogIn size={14} /> Sign in
            </Link>
          )}
        </div>
      </header>

      {/* Tiny hero */}
      <section className="container-hs pt-8 md:pt-16 pb-12">
        <FadeUp>
          <div className="hs-chip mb-6" data-testid="hero-status-chip">
            <span className="dot" /> HARS · HARSIGHT · DEV REVIEW
          </div>
        </FadeUp>
        <h1 className="font-display text-[clamp(2.6rem,6vw,5rem)] leading-[0.95]">
          <MaskedLines
            lines={[
              "Harsight CORE Suite",
              <><span className="text-warm italic">Review Room</span><span> & Development Center</span></>,
            ]}
            delay={0.15}
            stagger={0.12}
          />
        </h1>
        <FadeUp delay={0.6}>
          <p className="mt-6 text-lg text-muted-hs max-w-2xl">
            {isAuthed
              ? "Click any app to open it. One sign-in covers all six."
              : "Sign in once to open any of the six applications below."}
          </p>
        </FadeUp>
      </section>

      {/* Apps grid — the entire point */}
      <section className="container-hs pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-5">
          {APPS.map((app, i) => (
            <AppTile key={app.slug} app={app} delay={i * 0.06} isAuthed={isAuthed} />
          ))}
        </div>
      </section>

      {/* Tiny footer */}
      <footer className="container-hs pb-10 pt-6 border-t border-hs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-hs">
          © {new Date().getFullYear()} Harsight · Development Review Portal
        </div>
        <div className="flex gap-6 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-hs">
          <a href="https://harsight.com" target="_blank" rel="noreferrer" className="hs-link">harsight.com</a>
          <a href="https://harsrcm.com" target="_blank" rel="noreferrer" className="hs-link">harsrcm.com</a>
        </div>
      </footer>
    </div>
  );
}

function AppTile({ app, delay, isAuthed }) {
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const onClick = async () => {
    if (!isAuthed) {
      nav("/login", { state: { from: "/", intent: app.slug } });
      return;
    }
    setBusy(true);
    await launchApp(app);
    setBusy(false);
  };

  return (
    <motion.button
      onClick={onClick}
      disabled={busy}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className="hs-app-card block p-6 text-left h-full disabled:opacity-60 disabled:cursor-wait"
      data-testid={`app-tile-${app.slug}`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="hs-chapter-num">APP_{app.number}</span>
        <span className="hs-chip">
          <span className="dot" /> {app.status}
        </span>
      </div>

      <div className="relative aspect-[4/3] mb-5 hs-spotlight border border-hs">
        <img src={app.image} alt={app.title} className="w-full h-full object-cover" loading="lazy" />
      </div>

      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-warm mb-2">
        {app.tag}
      </div>
      <h3 className="font-display text-2xl leading-[1.05] mb-3">{app.title}</h3>

      <div className="flex flex-wrap gap-1.5 mb-5">
        {app.modules.slice(0, 4).map((m) => (
          <span key={m} className="hs-chip !py-0.5 !px-2 !text-[10px]">{m}</span>
        ))}
      </div>

      <div className="flex items-center justify-between border-t border-hs pt-4">
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-hs">
          {busy ? "Launching…" : isAuthed ? "Open" : "Sign in to open"}
        </span>
        {busy ? (
          <Loader2 size={16} className="animate-spin text-warm" />
        ) : (
          <ArrowUpRight size={18} className="text-warm" />
        )}
      </div>
    </motion.button>
  );
}
